import React from 'react';
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter';
import {prism} from 'react-syntax-highlighter/dist/esm/styles/prism';

const DeleteMemories = ({ onSectionChange }) => {
    return (
        <div className="api-reference-layout">
            <div className="api-content">
                <div className="api-header">
                    <div className="api-method-badge post">POST</div>
                    <h1>delete_memories - Delete Memories</h1>
                    <p className="api-description">
                        Delete memory data for a specified user
                    </p>
                </div>

                <div className="api-endpoint">
                    <div className="endpoint-url">
                        <span className="method post">POST</span>
                        <span className="url">/api/v2/memory/delete</span>
                    </div>
                </div>

                <div className="api-section">
                    <h2 id="authorization">Authorization</h2>
                    <div className="auth-item">
                        <div className="auth-header">
                            <span className="auth-name">Authorization</span>
                            <span className="auth-type">string</span>
                            <span className="auth-location">header</span>
                            <span className="required-badge">required</span>
                        </div>
                        <p>Bearer authentication header of the form Bearer &lt;token&gt;, where &lt;token&gt; is your API key.</p>
                    </div>
                </div>

                <div className="api-section">
                    <h2 id="request-body">Request Body</h2>
                    <div className="params-container">
                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">user_id</span>
                                <span className="param-type">string</span>
                                <span className="required-badge">required</span>
                            </div>
                            <p>Unique identifier for the user</p>
                            <div className="param-example">
                                <strong>Example:</strong>
                                <code>"user001"</code>
                            </div>
                        </div>

                        <div className="param-divider"></div>

                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">agent_id</span>
                                <span className="param-type">string</span>
                                <span className="optional-badge">optional</span>
                            </div>
                            <p>Agent identifier, if null deletes all user memories</p>
                            <div className="param-default">
                                <strong>Default:</strong>
                                <code>null</code>
                            </div>
                            <div className="param-example">
                                <strong>Example:</strong>
                                <code>"gpt001"</code>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="api-section">
                    <h2 id="response">Response</h2>
                    <div className="response-status">
                        <span className="status-code success">200</span>
                        <span className="content-type">application/json</span>
                    </div>

                    <div className="params-container">
                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">success</span>
                                <span className="param-type">boolean</span>
                            </div>
                            <p>Whether the operation was successful</p>
                        </div>

                        <div className="param-divider"></div>

                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">deleted_count</span>
                                <span className="param-type">integer</span>
                            </div>
                            <p>Number of memories deleted</p>
                        </div>
                    </div>

                </div>
            </div>

            <div className="api-demo-sidebar">
                <div className="demo-sticky">
                    <div className="demo-header">
                        <div className="demo-tabs">
                            <button className="demo-tab active">Python</button>
                            <button className="demo-tab">JavaScript</button>
                            <button className="demo-tab">cURL</button>
                        </div>
                    </div>

                    <div className="demo-content">
                        <h3 id="examples">Request Example</h3>
                        <SyntaxHighlighter
                            language="python"
                            style={prism}
                            customStyle={{
                                margin: 0,
                                padding: '20px',
                                background: 'white',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                borderRadius: '0 0 8px 8px',
                                border: '1px solid #e5e7eb'
                            }}
                        >
                            {`import os
from memu import MemuClient

# Initialize MemU client
memu_client = MemuClient(
    base_url="https://api.memu.so",
    api_key=os.getenv("MEMU_API_KEY")
)

# Delete memories for a specific agent
response = memu_client.delete_memories(
    user_id="user001",
    agent_id="gpt001"
)

if response.success:
    print(f"Successfully deleted {response.deleted_count} memories")
else:
    print("Failed to delete memories")

# Delete ALL memories for a user (use with extreme caution!)
all_memories_response = memu_client.delete_memories(
    user_id="user001",
    agent_id=None  # This will delete ALL memories for the user
)

print(f"Deleted {all_memories_response.deleted_count} total memories")

# Alternative: Delete memories for specific user only
user_specific_response = memu_client.delete_memories(
    user_id="user001"
    # agent_id is optional, omitting it deletes all memories
)

print(f"Operation successful: {user_specific_response.success}")
print(f"Total memories deleted: {user_specific_response.deleted_count}")`}
                        </SyntaxHighlighter>
                    </div>

                    <div className="response-section">
                        <div className="response-header">
                            <span className="response-status-code">200</span>
                            <span>Success Response (Specific Agent)</span>
                        </div>
                        <SyntaxHighlighter
                            language="json"
                            style={prism}
                            customStyle={{
                                margin: 0,
                                padding: '20px',
                                background: 'white',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                borderRadius: '0 0 8px 8px',
                                border: '1px solid #e5e7eb'
                            }}
                        >
                            {`{
  "success": true,
  "deleted_count": 15
}`}
                        </SyntaxHighlighter>
                    </div>

                    <div className="response-section">
                        <div className="response-header">
                            <span className="response-status-code">200</span>
                            <span>Success Response (All Memories)</span>
                        </div>
                        <SyntaxHighlighter
                            language="json"
                            style={prism}
                            customStyle={{
                                margin: 0,
                                padding: '20px',
                                background: 'white',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                borderRadius: '0 0 8px 8px',
                                border: '1px solid #e5e7eb'
                            }}
                        >
                            {`{
  "success": true,
  "deleted_count": 47
}`}
                        </SyntaxHighlighter>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DeleteMemories;

